import type { Sensor3P } from '../ScopingEditor/useGetState';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';

export const SENSOR3P_ICON_SIZE = 1.5;
/** Icon size for a sensor3p that is snapped to a spot — small enough to sit inside SPOT_ICON_SIZE. */
export const SENSOR3P_SNAPPED_ICON_SIZE = 1.0;

// Fixed IDs for the single shared set of sensor3p filters.
// All Sensor3pItem instances reference these instead of creating per-instance <defs>.
const SENSOR3P_FILTER_NORMAL_ID = 'sensor3p-filter-normal';
const SENSOR3P_FILTER_SELECTED_ID = 'sensor3p-filter-selected';
const SENSOR3P_FILTER_SNAPPED_ID = 'sensor3p-filter-snapped';

/**
 * Renders a single shared <defs> block with the two sensor3p color filters.
 * Mount this once inside the parent SVG so all Sensor3pItem instances can
 * reference the filters by ID without duplicating them.
 */
export function Sensor3pFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray */}
      <filter id={SENSOR3P_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={SENSOR3P_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Snapped: white icon to contrast against the filled spot */}
      <filter id={SENSOR3P_FILTER_SNAPPED_ID}>
        <feFlood floodColor="white" result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function Sensor3pItem({
  x = 0,
  y = 0,
  name,
  id,
  device_type,
  selected,
  selecting = false,
  fade = false,
  locked = false,
  isSnapped = false,
  onClick,
  ...rest
}: {
  selected: boolean;
  device_type: Sensor3P['device_type'];
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  isSnapped?: boolean;
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Sensor3P, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = SENSOR3P_ICON_SIZE / adjustedZoom;
  const snappedSize = SENSOR3P_SNAPPED_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;
  const iconName = device_type === 'motion_sensor' ? 'butlrmotionoutlined' : 'useroutlined';
  const displaySize = isSnapped ? snappedSize : size;
  const filterId = isSnapped
    ? SENSOR3P_FILTER_SNAPPED_ID
    : showAsSelected
    ? SENSOR3P_FILTER_SELECTED_ID
    : SENSOR3P_FILTER_NORMAL_ID;

  return (
    <g
      data-id="sensor3p-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        label={name}
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-${iconName}`}
              width={displaySize}
              height={displaySize}
              x={-displaySize / 2}
              y={-displaySize / 2}
              filter={`url(#${filterId})`}
              pointerEvents="none"
            />
            {/* sole hit target: circle sized to match the visible icon */}
            <circle cx={0} cy={0} r={size * 0.4} fill="transparent" pointerEvents="all" />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(Sensor3pItem);
